import { characters, products, type Character } from "@/lib/fragrance-data";

/**
 * Descriptive alt text for the Character Archive portraits.
 *
 * Rules for entries here:
 * - describe what is actually visible in the photograph (subject + setting),
 *   not just the note's name — the name is already read out from the heading
 *   next to the image.
 * - no "image of" / "photo of" prefixes (screen readers already announce that).
 * - keep it one sentence.
 */
export const CHARACTER_IMAGE_ALT: Record<string, string> = {
  "violet-the-ghost-in-the-garden":
    "Violet — deep purple violet blossoms and green leaves in low candlelight on dark stone",
  "clove-the-spiced-bloom":
    "Clove — dried clove buds scattered beside a small dish of spice on a shadowed surface",
  "star-anise-the-celestial-seed":
    "Star anise — eight-pointed star anise pods, one broken open, on dark weathered wood",
  "cedarwood-the-quiet-architect":
    "Cedarwood — split cedar timber and pale wood shavings showing the dry grain of the heartwood",
  "vetiver-the-root-of-shadows":
    "Vetiver — a tangle of dried vetiver roots and damp soil against a dark background",
  "rose-the-sovereign-bloom":
    "Rose — a full crimson rose in half-shadow with loose petals fallen around it",
  "labdanum-the-golden-shadow":
    "Labdanum — dark sticky cistus resin pooled in an amber-lit dish beside dried leaves",
  "amber-the-fossil-glow":
    "Amber — polished honey-coloured amber resin glowing against a dark cloth",
  "black-tea-the-steeped-hour":
    "Black tea — loose dried tea leaves spilled beside a steeped cup on aged paper",
  "cardamom-the-green-ember":
    "Cardamom — pale green cardamom pods, some split to show dark seeds, on stone",
  "frankincense-the-listening-smoke":
    "Frankincense — milky white frankincense tears in a censer with a thread of rising smoke",
  "guaiacwood-the-smoked-cathedral":
    "Guaiacwood — dense dark guaiac wood chips and a polished cross-section of the trunk",
  "hay-the-summer-keeping":
    "Hay — a bundle of sun-dried grasses tied with twine in warm low light",
  "ho-wood-the-pale-breath":
    "Ho wood — pale blond wood chips and a cut branch on a cool grey surface",
  "honey-the-slow-gold":
    "Honey — golden honey drawn in a slow thread from a dipper beside a piece of waxen comb",
  "juniper-berry-the-cold-blue":
    "Juniper berry — dusty blue juniper berries on a resinous evergreen sprig",
  "lavender-the-old-witness":
    "Lavender — a bound bundle of dried lavender stems with loose purple buds beneath",
  "lemon-the-quick-blade":
    "Lemon — a bright lemon cut open, peel curling beside it, on dark stone",
  "marshmallow-the-soft-trick":
    "Marshmallow — soft white marshmallow root pieces and a dusting of powdered sugar",
  "mastic-the-pale-resin":
    "Mastic — translucent pale resin tears gathered on rough stone",
  "myrrh-the-bitter-balm":
    "Myrrh — reddish-brown myrrh resin lumps in a shallow bowl in dim golden light",
  "oakmoss-the-forest-floor":
    "Oakmoss — grey-green lichen clinging to damp oak bark on the forest floor",
  "oakwood-the-slow-cask":
    "Oakwood — charred oak barrel staves showing blackened grain and a cut end",
  "palmarosa-the-rose-grass":
    "Palmarosa — long green palmarosa grass blades gathered in a loose sheaf",
  "patchouli-the-second-skin":
    "Patchouli — dried and crumbled patchouli leaves heaped on dark earth",
  "pink-pepper-the-bright-bite":
    "Pink pepper — clusters of rosy pink peppercorns on a fine branch",
  "rosemary-the-cold-altar":
    "Rosemary — needled rosemary sprigs laid across cool pale stone",
  "sandalwood-the-warm-prayer":
    "Sandalwood — creamy sandalwood billets and fine sawdust in warm light",
  "sweet-fennel-the-licorice-light":
    "Sweet fennel — fennel seed heads and loose green seeds on a weathered board",
  "tonka-bean-the-soft-coumarin":
    "Tonka bean — wrinkled black tonka beans, one sliced, on dark textured cloth",
  "tuberose-the-white-fever":
    "Tuberose — waxy white tuberose blossoms opening along a green stem in shadow",
  "vanilla-the-quiet-hearth":
    "Vanilla — cured vanilla pods, one split to show the seeds, tied together on dark wood",
};

/** Portrait alt text for a Character, with a descriptive fallback. */
export function getCharacterImageAlt(character: Character): string {
  return (
    CHARACTER_IMAGE_ALT[character.slug] ??
    `${character.name}, ${character.epithet} — a botanical still-life portrait of ${character.name.toLowerCase()} in low candlelight`
  );
}

const productsBySlug = new Map(products.map((p) => [p.slug, p]));
const productsByShopifyHandle = new Map(
  products.flatMap((p) => (p.shopifyHandle ? [[p.shopifyHandle, p] as const] : [])),
);

function localProduct(handle: string) {
  return productsBySlug.get(handle) ?? productsByShopifyHandle.get(handle);
}

/**
 * Alt text for a product photograph.
 *
 * Priority: Shopify-authored alt text → our curated `imageAlt` → a composed
 * sentence. Never a bare product title, which tells a screen-reader user
 * nothing the adjacent heading hasn't already said.
 */
export function getProductImageAlt({
  shopifyAlt,
  handle,
  title,
  subtitle,
}: {
  shopifyAlt?: string | null;
  handle?: string | null;
  title: string;
  subtitle?: string | null;
}): string {
  const fromShopify = shopifyAlt?.trim();
  if (fromShopify && fromShopify.toLowerCase() !== title.trim().toLowerCase()) {
    return fromShopify;
  }

  const local = handle ? localProduct(handle) : undefined;
  if (local?.imageAlt) return local.imageAlt;

  const descriptor = (subtitle ?? local?.subtitle ?? "").trim();
  return descriptor
    ? `${title}, ${descriptor} — a black glass natural eau de parfum bottle by House of Apocrypha`
    : `${title} — a black glass natural eau de parfum bottle by House of Apocrypha`;
}

/** Convenience wrapper used where only a character slug is available. */
export function getCharacterImageAltBySlug(slug: string): string | undefined {
  const character = characters.find((c) => c.slug === slug);
  return character ? getCharacterImageAlt(character) : CHARACTER_IMAGE_ALT[slug];
}
